// whereFragments — map the resolver's structure.where[] (the SAME fragment list
// the SR-Edit applicator indexes into) onto the render-side BooleanFragment
// shape used by the WHERE/HAVING pills. Rendering editable pills from this list
// guarantees pill index === modify/remove `fragmentIndex`, even under a
// top-level OR or a BETWEEN — eliminating the AND-only-vs-AND/OR split
// divergence that the local `splitTopLevelBoolean` fallback would otherwise
// reintroduce.

import type { BooleanNode, QueryStructure } from '../../../../sql-resolver/types';
import type { BooleanFragment } from './splitTopLevel';

export function whereFragmentsFromStructure(
  where: NonNullable<QueryStructure['where']>,
): BooleanFragment[] {
  return where.map((f) => ({
    start: f.range.startOffset,
    end: f.range.endOffset,
    text: f.text,
    leadingConnector: f.leadingConnector ?? null,
  }));
}

/** One emitted top-level WHERE node: its tree, its path-edit address, and the
 *  AND/OR connector that precedes it (threaded from the implicit parent). */
export interface EmittedWhereNode {
  readonly node: BooleanNode;
  readonly path: readonly number[];
  readonly leadingConnector?: 'AND' | 'OR';
}

/**
 * Walk the WHERE `BooleanNode` tree the SAME way the resolver's
 * `flattenBooleanTree` derives `structure.where[]` — transparent through
 * implicit precedence groups (threading their connector to non-first children),
 * opaque at leaves and explicit `(…)` groups — but return each emitted node
 * paired with its tree PATH (the path-addressed edit/remove address used by the
 * Rail) and threaded leading connector.
 *
 * The output is 1:1 and index-aligned with
 * `whereFragmentsFromStructure(structure.where)`, so the Inline pill view can
 * zip the two: a leaf renders as the flat fragment-indexed pill (unchanged),
 * while an explicit group expands into a tinted box of its own path-addressed
 * sub-pills instead of one opaque freeform-text pill.
 *
 * KEEP IN SYNC with `flattenBooleanTree`'s emit walk (same transparency rule).
 * The one deliberate difference: this walk does NOT replicate
 * `flattenBooleanTree`'s skip of an opaque node whose trimmed range is empty, so
 * its output is an order-preserving *superset*. Callers MUST guard on
 * `length === structure.where.length` (an equal-length subsequence is identical
 * element-for-element) and fall back when they differ — `FilterPills` does.
 */
export function flattenWhereTreeNodes(root: BooleanNode): EmittedWhereNode[] {
  const out: EmittedWhereNode[] = [];
  const emit = (node: BooleanNode, path: readonly number[], lead: 'AND' | 'OR' | undefined): void => {
    if (node.kind === 'group' && !node.explicit) {
      node.children.forEach((child, idx) =>
        emit(child, [...path, idx], idx === 0 ? lead : node.connector),
      );
      return;
    }
    out.push({ node, path, ...(lead ? { leadingConnector: lead } : {}) });
  };
  if (root.kind === 'group' && !root.explicit) {
    root.children.forEach((child, idx) => emit(child, [idx], idx === 0 ? undefined : root.connector));
  } else {
    emit(root, [], undefined);
  }
  return out;
}

export type WhereConnector = 'AND' | 'OR';

/**
 * The connector of the list a node at `path` sits in — the group at
 * `path.slice(0, -1)`. A lone condition (no parent group) reads as an AND
 * list: adding a sibling there is the footer's Add condition.
 */
export function parentConnectorOf(root: BooleanNode | null | undefined, path: readonly number[]): WhereConnector {
  let node: BooleanNode | undefined = root ?? undefined;
  for (const idx of path.slice(0, -1)) {
    if (node?.kind !== 'group') return 'AND';
    node = node.children[idx];
  }
  return node?.kind === 'group' ? node.connector : 'AND';
}

export function oppositeConnector(connector: WhereConnector): WhereConnector {
  return connector === 'AND' ? 'OR' : 'AND';
}
